import { PermissionCode, type AuthUser } from '@gb-ticket/shared-types';

import { hasPermission } from '@/platform/auth/permissions';
import type { AdminLinkGroup } from '@/platform/lib/admin';
import { ARTICLES_GROUPS_PATH, ARTICLES_ROOT } from '@/platform/lib/article-paths';

export const MASTER_DATA_ROOT = '/stammdaten';
export const MASTER_DATA_PARTNERS_PATH = `${MASTER_DATA_ROOT}/partner`;
export const MASTER_DATA_PARTNER_NEW_PATH = `${MASTER_DATA_PARTNERS_PATH}/neu`;
export const MASTER_DATA_PAYMENTS_PATH = `${MASTER_DATA_ROOT}/zahlungen`;
export const MASTER_DATA_CURRENCIES_PATH = `${MASTER_DATA_ROOT}/waehrungen`;
export const MASTER_DATA_UNITS_PATH = `${MASTER_DATA_ROOT}/einheiten`;
export const MASTER_DATA_COST_CENTERS_PATH = `${MASTER_DATA_ROOT}/kostenstellen`;
export const MASTER_DATA_EXCHANGE_PATH = `${MASTER_DATA_ROOT}/austausch`;

export function masterDataPartnerPath(id: string): string {
  return `${MASTER_DATA_PARTNERS_PATH}/${id}`;
}

export function masterDataHomePath(user: AuthUser | null | undefined): string {
  if (hasPermission(user, PermissionCode.MASTER_DATA_READ)) {
    return MASTER_DATA_PARTNERS_PATH;
  }
  if (hasPermission(user, PermissionCode.ARTICLES_READ)) {
    return ARTICLES_ROOT;
  }
  return MASTER_DATA_PARTNERS_PATH;
}

export function masterDataNavItems(
  user: AuthUser | null | undefined,
): { to: string; label: string }[] {
  if (!user) {
    return [];
  }
  const canMasterData = hasPermission(user, PermissionCode.MASTER_DATA_READ);
  const canArticles = hasPermission(user, PermissionCode.ARTICLES_READ);
  const canExchange =
    hasPermission(user, PermissionCode.MASTER_DATA_IMPORT) ||
    hasPermission(user, PermissionCode.MASTER_DATA_EXPORT);
  const items: { to: string; label: string }[] = [];
  if (canMasterData) {
    items.push({ to: MASTER_DATA_PARTNERS_PATH, label: 'Partner' });
  }
  if (canArticles) {
    items.push(
      { to: ARTICLES_ROOT, label: 'Artikel' },
      { to: ARTICLES_GROUPS_PATH, label: 'Gruppen' },
    );
  }
  if (canMasterData) {
    items.push(
      { to: MASTER_DATA_PAYMENTS_PATH, label: 'Zahlungen' },
      { to: MASTER_DATA_CURRENCIES_PATH, label: 'Währungen' },
      { to: MASTER_DATA_UNITS_PATH, label: 'Einheiten' },
      { to: MASTER_DATA_COST_CENTERS_PATH, label: 'Kostenstellen' },
    );
  }
  if (canExchange) {
    items.push({ to: MASTER_DATA_EXCHANGE_PATH, label: 'Import / Export' });
  }
  return items;
}

export function masterDataStartGroups(user: AuthUser): AdminLinkGroup[] {
  const canMasterData = hasPermission(user, PermissionCode.MASTER_DATA_READ);
  const canArticles = hasPermission(user, PermissionCode.ARTICLES_READ);
  const canExchange =
    hasPermission(user, PermissionCode.MASTER_DATA_IMPORT) ||
    hasPermission(user, PermissionCode.MASTER_DATA_EXPORT);
  if (!canMasterData && !canArticles) {
    return [];
  }

  const links: AdminLinkGroup['links'] = [];
  if (canMasterData) {
    links.push({
      id: 'partners',
      to: MASTER_DATA_PARTNERS_PATH,
      label: 'Geschäftspartner',
      description: 'Lieferanten, Adressen und Kontakte',
    });
  }
  if (canArticles) {
    links.push({
      id: 'articles',
      to: ARTICLES_ROOT,
      label: 'Artikel',
      description: 'Material, Dienstleistungen und Gruppen',
    });
  }
  if (canMasterData) {
    links.push(
      {
        id: 'payments',
        to: MASTER_DATA_PAYMENTS_PATH,
        label: 'Zahlungen',
        description: 'Bedingungen, Arten und Skonto',
      },
      {
        id: 'currencies',
        to: MASTER_DATA_CURRENCIES_PATH,
        label: 'Währungen',
        description: 'ISO-Codes und Nachkommastellen',
      },
      {
        id: 'units',
        to: MASTER_DATA_UNITS_PATH,
        label: 'Mengeneinheiten',
        description: 'Einheiten und Umrechnungen',
      },
      {
        id: 'cost-centers',
        to: MASTER_DATA_COST_CENTERS_PATH,
        label: 'Kostenstellen',
        description: 'Codes und Bezeichnungen',
      },
    );
    if (canExchange) {
      links.push({
        id: 'exchange',
        to: MASTER_DATA_EXCHANGE_PATH,
        label: 'Import und Export',
        description: 'CSV-Vorschau, Übernahme und Export',
      });
    }
  }

  return [
    {
      id: 'stammdaten',
      heading: 'Stammdaten',
      links,
    },
  ];
}
